import { useEffect, useRef } from "react";
import type { MouseEvent, PointerEvent as ReactPointerEvent } from "react";
import css from "./TextPad.module.css";

/**
 * Клавиатура TextPad — раскладки/ряды эталона design-references/
 * textpad.js:37-40,72-89 (1:1). Вынесено из TextPad.tsx (лимит новых
 * файлов ≤250 строк). Три слоя: ЙЦУКЕН / qwerty / цифры-символы
 * (num+shift = SYM). Ряд 3 обрамляют shift (dblclick = caps) и ⌫
 * (long-press у хоста); нижний ряд — цикл слоёв, ⏎ при multiline,
 * «Пробел», «Готово». Флэш клавиши 160мс — императивно classList
 * (эталон :68), без state на каждый тап.
 */

export type TextPadLayer = "ru" | "en" | "num";

/* Раскладки эталона textpad.js:37-40. */
const RU = ["йцукенгшщзхъ", "фывапролджэ", "ячсмитьбю"];
const EN = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];
const NUM = ["1234567890", '-/:;()₽&@"', ".,?!'%#"];
const SYM = ["[]{}#%^*+=", "_\\|~<>€$£·", ".,?!'`—"];

interface TextPadKeysProps {
  layer: TextPadLayer;
  shift: boolean;
  multiline?: boolean;
  /** Постоянный ряд цифр над буквами (24.08, комментарий ДК); на num-слое не дублируется. */
  numRow?: boolean;
  /** Базовый символ; upper-case по shift применяет хост (эталон :65). */
  onChar: (ch: string) => void;
  onShiftTap: () => void;
  onShiftDbl: () => void;
  onCycle: () => void;
  onDone: () => void;
  onDelClick: () => void;
  onDelDown: () => void;
  onDelUp: () => void;
  /** 06.09 (владелец: «отсутствует Ё»): лонгпресс 450мс на «е» — хост меняет последний символ base→alt. */
  onLongChar?: (base: string, alt: string) => void;
  /** 27.09 (владелец): ◀ ▶ — каретка на символ; удержание — автоповтор, как у ⌫. */
  onCaret?: (d: -1 | 1) => void;
}

const FL = css.fl ?? "";
/* Лонгпресс-альтернативы слоя ru (ъ не нужен — есть в ряду). */
const LONG: Record<string, string> = { "е": "ё" };
const LONG_MS = 450;
const LONG_MOVE = 10;
/* ◀ ▶ удержанием — темп автоповтора ⌫ (TextPad): старт 450мс, затем 90мс, после 15 шагов 45мс. */
const REP_MS = 90, REP_FAST_MS = 45, REP_FAST_AFTER = 15;

function flash(el: HTMLButtonElement): void {
  if (!FL) return;
  el.classList.add(FL);
  window.setTimeout(() => el.classList.remove(FL), 160);
}

function IcoDel() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M9 5h12v14H9l-6-7z" />
      <path d="M12.5 9.5l5 5M17.5 9.5l-5 5" />
    </svg>
  );
}

function IcoArr({ d }: { d: -1 | 1 }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d < 0 ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
    </svg>
  );
}

function IcoSh() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 4l8 9h-5v7H9v-7H4z" />
    </svg>
  );
}

export function TextPadKeys(p: TextPadKeysProps) {
  const lines = p.layer === "ru" ? RU : p.layer === "en" ? EN : p.shift ? SYM : NUM;
  const up = p.shift && p.layer !== "num";
  /* Двойной тап shift = caps ловим сами по времени (14.08): preventDefault
     pointerdown-ввода глушит синтетические click/dblclick. */
  const shiftTapAt = useRef(0);
  const click = (fn: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
    flash(e.currentTarget);
    fn();
  };
  /* Ввод по КАСАНИЮ (pointerdown), как у системных клавиатур — click ждёт
     отпускания пальца и давал ощутимую задержку печати (жалоба 14.08).
     preventDefault: кнопка не берёт фокус, хвостовой click не рождается. */
  const press = (fn: () => void) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    flash(e.currentTarget);
    fn();
  };
  /* Лонгпресс «е»→«ё» (06.09): тап вставляет «е» как обычно (латентность не
     трогаем); держит 450мс без сдвига > 10px — хост заменяет её на «ё».
     Сдвиг проверяем сами по pointermove: при захвате указателя зоной
     (росчерк) и touch-захвате кнопкой pointerleave не гарантирован. */
  const lp = useRef<{ t: number; x: number; y: number }>({ t: 0, x: 0, y: 0 });
  const lpStop = () => { window.clearTimeout(lp.current.t); lp.current.t = 0; };
  const lpDown = (ch: string, alt: string) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    press(() => p.onChar(ch))(e);
    lpStop();
    lp.current = { x: e.clientX, y: e.clientY, t: window.setTimeout(() => { lp.current.t = 0; p.onLongChar?.(ch, alt); }, LONG_MS) };
  };
  const lpMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (lp.current.t && Math.hypot(e.clientX - lp.current.x, e.clientY - lp.current.y) > LONG_MOVE) lpStop();
  };
  /* ◀ ▶ (27.09, владелец: «перемещать курсор, чтобы стереть только одну букву»): шаг на касание,
     удержание ведёт каретку дальше. Без data-key — росчерк с этих клавиш не начинается. */
  const rep = useRef(0);
  const repStop = () => { window.clearTimeout(rep.current); rep.current = 0; };
  useEffect(() => repStop, []);
  const caretKey = (d: -1 | 1) => {
    const down = (e: ReactPointerEvent<HTMLButtonElement>) => {
      press(() => p.onCaret?.(d))(e);
      repStop();
      const tick = (n: number) => { p.onCaret?.(d); rep.current = window.setTimeout(() => tick(n + 1), n >= REP_FAST_AFTER ? REP_FAST_MS : REP_MS); };
      rep.current = window.setTimeout(() => tick(0), LONG_MS);
    };
    return (
      <button type="button" className={`${css.key} ${css.fn} ${css.arr}`} aria-label={d < 0 ? "Курсор влево" : "Курсор вправо"}
        onPointerDown={down} onPointerUp={repStop} onPointerLeave={repStop} onPointerCancel={repStop} onContextMenu={(e) => e.preventDefault()}>
        <IcoArr d={d} />
      </button>
    );
  };
  /* Буквенная клавиша: data-key — геометрия для росчерка (useGlide); num-слой без него. */
  const letter = (ch: string, k: string) => {
    const alt = p.layer === "ru" ? LONG[ch] : undefined;
    const key = p.layer === "num" ? undefined : ch;
    if (!alt) return <button key={k} type="button" className={css.key} data-key={key} onPointerDown={press(() => p.onChar(ch))}>{up ? ch.toUpperCase() : ch}</button>;
    return (
      <button key={k} type="button" className={`${css.key} ${css.lp}`} data-key={key} onPointerDown={lpDown(ch, alt)}
        onPointerMove={lpMove} onPointerUp={lpStop} onPointerLeave={lpStop} onPointerCancel={lpStop}>
        {up ? ch.toUpperCase() : ch}
        <span className={css.hint} aria-hidden="true">{up ? alt.toUpperCase() : alt}</span>
      </button>
    );
  };
  return (
    <>
      {p.numRow && p.layer !== "num" ? (
        <div className={css.row}>
          {[..."1234567890"].map((d) => (
            <button key={d} type="button" className={css.key} onPointerDown={press(() => p.onChar(d))}>{d}</button>
          ))}
        </div>
      ) : null}
      {lines.map((line, i) => (
        <div key={i} className={css.row}>
          {i === 2 ? (
            <button
              type="button"
              className={`${css.key} ${css.fn} ${p.shift ? css.on : ""}`}
              aria-label="Shift"
              onPointerDown={press(() => {
                const now = Date.now();
                if (now - shiftTapAt.current < 300) p.onShiftDbl();
                else p.onShiftTap();
                shiftTapAt.current = now;
              })}
            >
              <IcoSh />
            </button>
          ) : null}
          {[...line].map((ch, j) => letter(ch, `${j}-${ch}`))}
          {i === 2 ? (
            <button
              type="button"
              className={`${css.key} ${css.fn}`}
              aria-label="Стереть"
              onPointerDown={press(() => { p.onDelClick(); p.onDelDown(); })}
              onPointerUp={p.onDelUp}
              onPointerLeave={p.onDelUp}
              onPointerCancel={p.onDelUp}
            >
              <IcoDel />
            </button>
          ) : null}
        </div>
      ))}
      <div className={css.row}>
        <button type="button" className={`${css.key} ${css.fn}`} onPointerDown={press(p.onCycle)}>
          {p.layer === "ru" ? "EN" : p.layer === "en" ? "123" : "АБВ"}
        </button>
        {p.multiline ? (
          <button type="button" className={`${css.key} ${css.fn}`} aria-label="Перенос строки" onPointerDown={press(() => p.onChar("\n"))}>
            ⏎
          </button>
        ) : null}
        {p.onCaret ? caretKey(-1) : null}
        {/* «,» и «.» у пробела на всех слоях (24.08, владелец) — как у системных. */}
        <button type="button" className={css.key} onPointerDown={press(() => p.onChar(","))}>,</button>
        <button type="button" className={`${css.key} ${css.sp}`} onPointerDown={press(() => p.onChar(" "))}>
          Пробел
        </button>
        <button type="button" className={css.key} onPointerDown={press(() => p.onChar("."))}>.</button>
        {p.onCaret ? caretKey(1) : null}
        {/* Готово — единственная клавиша на click: закрытие панели на pointerdown
            отпустило бы хвостовой click в элемент, оказавшийся под пальцем. */}
        <button type="button" className={`${css.key} ${css.ok}`} onClick={click(p.onDone)}>
          Готово
        </button>
      </div>
    </>
  );
}
